// crewbit-admin/src/components/money/AuthoringActionFooter.tsx
//
// Spec: §InvoiceAuthoring §3c. Sticky footer at the bottom of the
// MoneyInvoiceAuthoringPanel content area. Three terminal action buttons:
//   - Send now (primary accent)
//   - Save draft (neutral)
//   - Send & record cash (secondary green; gated on total > 0)
// AutoSaveIndicator inline on the left.
//
// Footer pins via `position: sticky; bottom: -20px` to stick to the bottom
// of SlidePanel's scrollable content area (which has 20px padding).

import { Loader2 } from 'lucide-react';
import { AutoSaveIndicator } from './AutoSaveIndicator';
import { palette, radii, spacing, transition, typography } from '../../styles/tokens';

type Action = 'send' | 'draft' | 'cash' | null;

interface Props {
  isAutoSaving: boolean;
  lastSavedAt: string | null;
  error: Error | null;
  onSendNow: () => void;
  onSaveDraft: () => void;
  onSendAndCash: () => void;
  onRetrySave: () => void;
  busy: Action;
  /** Disabled because validation fails (no client/no valid line). */
  validationDisabled: boolean;
  /** "Send & record cash" gates additionally on total > 0. */
  cashAvailable: boolean;
}

export function AuthoringActionFooter({
  isAutoSaving,
  lastSavedAt,
  error,
  onSendNow,
  onSaveDraft,
  onSendAndCash,
  onRetrySave,
  busy,
  validationDisabled,
  cashAvailable,
}: Props) {
  const anyBusy = busy !== null;
  const sendDisabled = validationDisabled || anyBusy;
  const draftDisabled = validationDisabled || anyBusy;
  const cashDisabled = validationDisabled || anyBusy || !cashAvailable;

  return (
    <div style={DOCK}>
      <div style={LEFT}>
        <AutoSaveIndicator
          isAutoSaving={isAutoSaving}
          lastSavedAt={lastSavedAt}
          error={error}
          onRetry={onRetrySave}
        />
      </div>

      <div style={BTN_ROW}>
        <button
          type="button"
          onClick={onSaveDraft}
          disabled={draftDisabled}
          style={{
            ...BTN_BASE,
            ...BTN_NEUTRAL,
            ...(draftDisabled ? BTN_DISABLED : null),
          }}
        >
          {busy === 'draft' ? <SpinnerLabel color={palette.g700} text="Saving…" /> : 'Save draft'}
        </button>

        <button
          type="button"
          onClick={onSendAndCash}
          disabled={cashDisabled}
          style={{
            ...BTN_BASE,
            ...BTN_SECONDARY,
            ...(cashDisabled ? BTN_DISABLED : null),
          }}
          title={!cashAvailable ? 'Add line items to enable cash payment' : undefined}
        >
          {busy === 'cash'
            ? <SpinnerLabel color={palette.green} text="Recording…" />
            : 'Send & record cash'}
        </button>

        <button
          type="button"
          onClick={onSendNow}
          disabled={sendDisabled}
          style={{
            ...BTN_BASE,
            ...BTN_PRIMARY,
            ...(sendDisabled ? BTN_DISABLED_PRIMARY : null),
          }}
        >
          {busy === 'send' ? <SpinnerLabel color={palette.white} text="Sending…" /> : 'Send now'}
        </button>
      </div>
    </div>
  );
}

function SpinnerLabel({ color, text }: { color: string; text: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <Loader2 size={14} color={color} strokeWidth={2} className="autosave-spin" />
      {text}
    </span>
  );
}

const DOCK: React.CSSProperties = {
  position: 'sticky',
  bottom: -20,
  marginLeft: -20,
  marginRight: -20,
  marginTop: spacing.md,
  padding: `${spacing.sm}px ${spacing.md}px`,
  background: palette.white,
  borderTop: `1px solid ${palette.g100}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: spacing.sm,
  flexWrap: 'wrap',
  zIndex: 1,
};

const LEFT: React.CSSProperties = {
  flexShrink: 0,
};

const BTN_ROW: React.CSSProperties = {
  display: 'flex',
  gap: spacing.sm,
  flexWrap: 'wrap',
  justifyContent: 'flex-end',
};

const BTN_BASE: React.CSSProperties = {
  padding: '9px 16px',
  borderRadius: radii.full,
  fontSize: typography.size.caption,
  fontWeight: typography.weight.semibold,
  fontFamily: typography.fontFamily,
  cursor: 'pointer',
  transition: `background ${transition.fast}, color ${transition.fast}`,
  border: '1px solid transparent',
};

const BTN_PRIMARY: React.CSSProperties = {
  background: palette.accent,
  color: palette.white,
};

const BTN_NEUTRAL: React.CSSProperties = {
  background: palette.g100,
  color: palette.g700,
};

const BTN_SECONDARY: React.CSSProperties = {
  background: palette.greenWash,
  color: palette.green,
  borderColor: palette.green,
};

const BTN_DISABLED: React.CSSProperties = {
  opacity: 0.5,
  cursor: 'not-allowed',
};

const BTN_DISABLED_PRIMARY: React.CSSProperties = {
  background: palette.g300,
  color: palette.white,
  cursor: 'not-allowed',
};
